import { Box, Button } from '@factorialco/deprecated-design-system'
import {
  AddCardItmanagementSoftwareSetupCardholderDataInput,
  BankingCardLimitPeriodEnum,
  CardholderInformationDocument,
} from 'generated/resources/graphql'

import DayPicker from 'modules/core/components/DayPicker'
import Modal from 'modules/core/components/Modal'
import MoneyField from 'modules/core/components/MoneyField'
import SelectField from 'modules/core/components/SelectField'
import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { dateBackendToUI, dateUIToBackend } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import DialCodeSelector from 'modules/expenses/components/common/DialCodeSelector'
import { getPhoneCode } from 'modules/expenses/lib/countryCodes'
import { useCreateCard } from 'modules/it_management/hooks/useCreateCard'

interface FooterProps {
  onClose: () => void
  disabled: boolean
}

interface ModalProps {
  onClose: () => void
  softwareSetupId: number
}

const Footer = ({ onClose, disabled }: FooterProps) => {
  return (
    <Box flexDirection="row" gap="s16">
      <Button type="secondary" onClick={onClose}>
        {i18n.t('common.cancel')}
      </Button>
      <Button disabled={disabled} submit>
        {i18n.t('common.confirm')}
      </Button>
    </Box>
  )
}

interface CreateCardForm {
  spendingAmount: string
  spendingPeriod: BankingCardLimitPeriodEnum
  birthDate: string
  countryCode: string
  phoneNumber: string
}

const CreateCardModal = ({ onClose, softwareSetupId }: ModalProps) => {
  const { employee } = useCurrentSession()
  const { data: cardholderInformation, loading } = useQuery(CardholderInformationDocument, {
    variables: {
      employeeId: Number(employee?.id),
      creationSource: 'software',
    },
    skip: !employee,
  })

  const currentInformation = cardholderInformation?.employees?.employee
  const locale = useCurrentSession().access.locale
  const cardholder = currentInformation?.bankingCardholder
  const cardholderExists = !!cardholder

  const { createCard } = useCreateCard()
  const form = useForm<CreateCardForm>({
    defaultValues: {
      spendingAmount: '1000',
      spendingPeriod: 'monthly',
      birthDate: currentInformation?.birthdayOn || '',
      countryCode: currentInformation?.phoneCode || getPhoneCode(currentInformation?.country) || '',
      phoneNumber: currentInformation?.phoneNumber || '',
    },
    validate: (data) => {
      const errors: FormErrors<CreateCardForm> = {}

      if (!data.spendingAmount) {
        errors.spendingAmount = i18n.t(
          'it_management.software_setups.cards.errors.spending_amount_mandatory'
        )
      }
      if (parseInt(data.spendingAmount) < 0) {
        errors.spendingAmount = i18n.t(
          'it_management.software_setups.cards.errors.spending_amount_positive'
        )
      }
      if (!cardholderExists && !data.birthDate) {
        errors.birthDate = i18n.t('it_management.software_setups.cards.errors.birth_date_mandatory')
      }

      if (!cardholderExists && !data.phoneNumber) {
        errors.phoneNumber = i18n.t(
          'it_management.software_setups.cards.errors.mobile_phone_mandatory'
        )
      }

      if (!cardholderExists && !data.countryCode) {
        errors.countryCode = i18n.t(
          'it_management.software_setups.cards.errors.mobile_phone_mandatory'
        )
      }
      return errors
    },
    onSubmit: async (data, { setErrors }) => {
      const amountInCents = parseInt(data.spendingAmount) * 100

      let cardholderData: AddCardItmanagementSoftwareSetupCardholderDataInput | undefined
      if (!cardholderExists) {
        cardholderData = {
          dateOfBirth: data.birthDate,
          email: currentInformation!.email!,
          firstName: currentInformation!.firstName,
          lastName: currentInformation!.lastName,
          phoneNumber: data.phoneNumber,
          phoneCountryCode: data.countryCode,
        }
      }
      const res = await createCard(
        softwareSetupId,
        amountInCents,
        data.spendingPeriod,
        employee!.id + '',
        cardholderData
      )

      const result = handleResult(res, ({ spendingAmount }) => {
        setErrors({
          spendingAmount,
        })
      })

      if (result.success) {
        track(trackingEvents.itManagement.createSoftwareCard)
        onClose()
        app.setFlash({
          type: 'correct',
          message: i18n.t('it_management.software_setups.cards.alerts.create_success'),
        })
      } else {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('it_management.software_setups.cards.errors.error_create'),
        })
      }

      return result
    },
  })

  if (!employee || !cardholderInformation) {
    return null
  }

  return (
    <Modal
      title={i18n.t('it_management.initial_setup.wizard.steps.costs.title')}
      description={i18n.t('it_management.initial_setup.wizard.steps.costs.description')}
      illustration="credit-card"
      onClose={onClose}
      onSubmit={() => form.handleSubmit()}
      footer={<Footer onClose={onClose} disabled={loading || form.isSubmitting || !form.isValid} />}
    >
      <Box gap="s24" width="s400">
        <MoneyField
          mandatory
          placeholder="0"
          currency={cardholder?.currency || currentInformation?.company?.currency || 'EUR'}
          error={form.errors.spendingAmount}
          label={i18n.t('it_management.initial_setup.wizard.costs_form.budget')}
          value={form.values.spendingAmount}
          onChange={(value: string) => form.setValues({ ...form.values, spendingAmount: value })}
        />

        <SelectField
          mandatory
          label={i18n.t('it_management.initial_setup.wizard.costs_form.period')}
          options={[
            {
              label: i18n.t('common.monthly'),
              value: 'monthly',
            },
            {
              label: i18n.t('common.yearly'),
              value: 'yearly',
            },
          ]}
          value={form.values.spendingPeriod}
          error={form.errors.spendingPeriod}
          onChange={(value: string) =>
            form.setValues({
              ...form.values,
              spendingPeriod: value as BankingCardLimitPeriodEnum,
            })
          }
        />

        {!cardholderExists && (
          <>
            <DayPicker
              label={i18n.t('it_management.initial_setup.wizard.cardholder_form.birth_date')}
              onChange={(value) => {
                form.setValues({ ...form.values, birthDate: dateUIToBackend(value) })
              }}
              value={form.values.birthDate ? dateBackendToUI(form.values.birthDate) : ''}
              mandatory
              error={form.errors.birthDate}
            />

            <Box flexDirection="row" gap="s16">
              <Box flexGrow>
                <DialCodeSelector
                  label={i18n.t('it_management.initial_setup.wizard.cardholder_form.country_code')}
                  onChange={(value) => form.setValues({ ...form.values, countryCode: value })}
                  value={form.values.countryCode}
                  locale={locale}
                  mandatory
                />
              </Box>

              <Box>
                <TextField
                  label={i18n.t('it_management.initial_setup.wizard.cardholder_form.phone_number')}
                  onChange={(value) => form.setValues({ ...form.values, phoneNumber: value })}
                  value={form.values.phoneNumber}
                  mandatory
                  error={form.errors.phoneNumber}
                />
              </Box>
            </Box>
          </>
        )}
      </Box>
    </Modal>
  )
}

export default CreateCardModal
